import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Smartphone, Clock, Calculator, ShieldCheck, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { SEONavbar, SEOFooter } from '../components/SEOLayout';
import PublicChatWidget from '../components/PublicChatWidget';

const FEATURES = [
  { icon: MapPin, title: 'GPS-geofence per byggarbetsplats', text: 'Sätt en radie kring varje projekt. Anställda kan endast stämpla in när de är fysiskt på plats — ingen mer fusk eller glömda stämplingar.' },
  { icon: Smartphone, title: 'Worker-app som funkar offline', text: 'PWA installeras på telefonen utan AppStore. Stämpla in/ut även utan täckning — synkas så snart nätet kommer tillbaka.' },
  { icon: Clock, title: 'OB, kvällstid och övertid automatiskt', text: 'Kollektivavtals­regler (Byggnads, Måleri, VVS) räknas automatiskt. Kvällstid efter 17:00, OB på helger, dubbel­tid vid övertid — utan manuell input.' },
  { icon: Calculator, title: 'Auto-export till löneprogram', text: 'Färdiga lönefiler för Visma, Fortnox, Bokio och Edison. Inga manuella tabeller, inga fel som kostar tusentals kronor.' },
];

const FAQ = [
  { q: 'Varför digital tidrapportering istället för pappersrapporter?', a: 'Pappersrapporter försvinner, är svårt­läsliga och kräver flera timmars manuellt arbete varje vecka. Digital tidrapportering med GPS-validering eliminerar bortglömda timmar — företag sparar typiskt 15–25% i lönekostnader inom 3 månader.' },
  { q: 'Måste anställda ha smartphone?', a: 'Ja, men appen funkar på alla telefoner — både Android (från 2018) och iPhone (från iOS 13). Installation tar 30 sekunder via QR-kod, ingen AppStore behövs (PWA).' },
  { q: 'Vad händer om någon glömmer stämpla ut?', a: 'Admin får automatisk notis kl 18:00 om någon är "ut­glömd". Anställda kan stämpla ut hemifrån även om de var utanför geofencen — bara IN-stämplingen kräver platsen.' },
  { q: 'Funkar det för anställda på flera projekt samma dag?', a: 'Ja. Operio är ett av få system som tillåter att vara instämplad på FLERA projekt samtidigt — perfekt om en montör delar dagen mellan två villabyggen.' },
  { q: 'Hur räknas raster?', a: 'Antingen automatiskt (rast­avdrag enligt schema, t.ex. 30 min vid >6h pass) eller manuellt per stämpling. Inställning per användare.' },
];

const TidrapporteringPage = () => (
  <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }}>
    <SEO
      title="Tidrapportering för bygg med GPS – stämpling, OB & lön | Operio"
      description="Digital tidrapportering för byggföretag. GPS-stämpling per arbetsplats, automatiska OB-regler, lönexport och rätt löner varje månad. 14 dagar gratis."
      canonical="/tidrapportering-bygg"
      keywords="tidrapportering bygg, stämplingssystem bygg, tidrapportering app bygg, lönehantering bygg, GPS stämpling, OB-regler bygg"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Operio Tidrapportering',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'iOS, Android, Web',
        offers: { '@type': 'Offer', price: '1990', priceCurrency: 'SEK' },
        description: 'GPS-baserad tidrapportering med automatiska OB-regler och lönexport för svenska bygg-, mark- och VVS-företag.',
      }}
    />
    <SEONavbar activePage="tid" />

    <main className="pt-24 sm:pt-28 pb-20">
      <section className="px-5 sm:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-4">
            <Sparkles size={14} /> För svenska byggare och entreprenörer
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A] leading-[1.05]"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Tidrapportering för bygg — med GPS som inte ljuger
          </h1>
          <p className="text-base sm:text-lg text-[#6B6560] mt-5 max-w-2xl mx-auto leading-relaxed">
            Stämpla in på rätt projekt, OB räknas automatiskt och lön­filen ligger klar för Fortnox.
            Slut på pappers­rapporter och bortglömda timmar.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/register"><Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-8 text-base rounded-full shadow-lg shadow-[#E8613A]/30">Testa gratis i 14 dagar <ArrowRight size={18} className="ml-2" /></Button></Link>
            <Link to="/priser"><Button variant="outline" className="h-12 px-8 text-base rounded-full">Se priser</Button></Link>
          </div>
          <p className="text-xs text-[#7A7570] mt-4">Inga kortuppgifter · 5 min att komma igång · PWA — ingen AppStore</p>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20 sm:mt-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Varför byggare väljer Operio
            </h2>
            <p className="text-[#6B6560] mt-3 max-w-2xl mx-auto">Byggt för fält­arbete — inte för kontoret.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <article key={i} className="bg-white border border-[#E8E2D9] rounded-2xl p-6">
                  <div className="w-11 h-11 bg-[#E8613A]/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#E8613A]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1A1A1A]">{f.title}</h3>
                  <p className="text-sm text-[#6B6560] mt-2 leading-relaxed">{f.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20 sm:mt-28">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-[#E8E2D9] p-8 sm:p-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Så fungerar en typisk arbetsdag
          </h2>
          <ol className="space-y-5 mt-6">
            {[
              { t: '06:45 — Snickaren öppnar appen', d: 'Geofence-validering ser att han är på "Villa Eriksson, Vänersborg". Klickar "Stämpla in" — kvitto kommer direkt.' },
              { t: '12:15 — Rast 30 min', d: 'Avdrag autom., regel "≥6h = lunchrast". Inget att tänka på.' },
              { t: '17:45 — Stämplar ut', d: 'Utstämpling tillåts oavsett plats (bra om kollega kör hem honom). OB-tid 17:00–17:45 räknas automatiskt med 20% tillägg.' },
              { t: 'Söndag — Admin kör lönexport', d: 'Filen ligger redo i Fortnox-format. Totalt 38h ordinarie + 2h OB = 42h reg., 38 + (2×1.2) = 40.4h löne­utbetalning.' },
            ].map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex-shrink-0 w-9 h-9 bg-[#E8613A] text-white rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                <div>
                  <p className="font-bold text-[#1A1A1A]">{step.t}</p>
                  <p className="text-sm text-[#6B6560] mt-0.5">{step.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20 sm:mt-28">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-8 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vanliga frågor
          </h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group bg-white border border-[#E8E2D9] rounded-xl px-5 py-4 open:shadow-sm">
                <summary className="cursor-pointer text-base font-bold text-[#1A1A1A] flex justify-between items-center list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-[#E8613A] text-xl flex-shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-[#6B6560] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-20">
        <div className="max-w-4xl mx-auto bg-[#1A1A1A] rounded-2xl px-6 py-12 sm:px-12 sm:py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Sluta jaga timmar — börja räkna dem rätt
          </h2>
          <p className="text-white/70 mt-3 max-w-md mx-auto text-sm">14 dagar gratis. Lägg till hela personalen, sätt geofencen och kör.</p>
          <Link to="/register" className="inline-block mt-6">
            <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-8 text-base rounded-full shadow-lg shadow-[#E8613A]/30">Kom igång gratis <ArrowRight size={18} className="ml-2" /></Button>
          </Link>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-xs text-white/50">
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} /> Inga kortuppgifter</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck size={12} /> BankID & GDPR</span>
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 mt-16">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Läs vidare</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <Link to="/bygg-kalkyl-program" className="bg-white border border-[#E8E2D9] rounded-xl p-4 hover:border-[#E8613A]/40 transition-colors">
              <p className="font-bold text-[#1A1A1A]">Bygg kalkyl­program</p>
              <p className="text-[#6B6560] text-xs mt-1">AI som räknar offerten.</p>
            </Link>
            <Link to="/blogg/digitalisering-byggbranschen-guide" className="bg-white border border-[#E8E2D9] rounded-xl p-4 hover:border-[#E8613A]/40 transition-colors">
              <p className="font-bold text-[#1A1A1A]">Digitalisering i byggbranschen</p>
              <p className="text-[#6B6560] text-xs mt-1">Komplett guide för 2026.</p>
            </Link>
            <Link to="/priser" className="bg-white border border-[#E8E2D9] rounded-xl p-4 hover:border-[#E8613A]/40 transition-colors">
              <p className="font-bold text-[#1A1A1A]">Se priser</p>
              <p className="text-[#6B6560] text-xs mt-1">Från 1 990 kr/mån.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>

    <SEOFooter />
    <PublicChatWidget />
  </div>
);

export default TidrapporteringPage;
